/**
 * DocumentReader — /documents/$id/read  (UX hardening: in-app reader addon)
 *
 * A clean, branded read-only view of a controlled document's body — the missing
 * "read the document" experience (previously you could only edit it or see its
 * metadata). Renders the stored content with an auto-generated table of
 * contents, a status header, a controlled-copy print (watermarked), and an
 * Edit hand-off. Reuses the content + detail feeds.
 */
import { useMemo } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import { useDocumentDetailQuery } from '../DocumentDetail/useDocumentDetailQuery';
import { useDocumentContentQuery } from '../DocumentEditor/useDocumentContent';
import styles from './DocumentReader.module.css';

/** Inject stable ids into h2/h3 and collect a table of contents. */
function withToc(html: string): { html: string; toc: Array<{ id: string; text: string; level: 2 | 3 }> } {
  const toc: Array<{ id: string; text: string; level: 2 | 3 }> = [];
  let i = 0;
  const out = html.replace(/<h([23])>(.*?)<\/h\1>/gi, (_m, lvl: string, inner: string) => {
    const id = `sec-${i++}`;
    const text = inner.replace(/<[^>]+>/g, '').trim();
    toc.push({ id, text, level: Number(lvl) as 2 | 3 });
    return `<h${lvl} id="${id}">${inner}</h${lvl}>`;
  });
  return { html: out, toc };
}

export function DocumentReader() {
  const { id } = useParams({ strict: false }) as { id: string };
  const { t, i18n } = useTranslation();
  const detailQ = useDocumentDetailQuery(id);
  const contentQ = useDocumentContentQuery(id);
  const dateFmt = useMemo(() => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }), [i18n.language]);

  const { html, toc } = useMemo(() => withToc(contentQ.data?.content_html ?? ''), [contentQ.data]);
  const d = detailQ.data;
  const status = d?.status ?? '';
  const statusBand = status === 'EFFECTIVE' ? styles.stEffective : status === 'DRAFT' ? styles.stDraft : styles.stReview;

  return (
    <main className={styles.page}>
      {/* Controlled-copy print furniture (print-only). */}
      <div className={styles.printWatermark} aria-hidden="true">UNCONTROLLED WHEN PRINTED</div>
      <div className={styles.printFooter} aria-hidden="true">
        {d ? `${d.doc_number} · ${status}` : id} · {t('reader.printedBy', { defaultValue: 'Printed {{t}} — verify the current controlled version in QWise.', t: new Date().toLocaleString() })}
      </div>

      <header className={styles.header}>
        <nav className={styles.crumbs} aria-label="Breadcrumb">
          <Link to="/documents/$id" params={{ id }} className={styles.crumb}>{t('reader.back', { defaultValue: 'Document' })}</Link>
          <span aria-hidden>/</span>
          <span className={styles.crumbCurrent}>{t('reader.read', { defaultValue: 'Read' })}</span>
        </nav>
        <div className={styles.titleRow}>
          <div className={styles.titleBlock}>
            <h1 className={styles.title}>
              <span className={styles.docNum}>{d?.doc_number ?? id}</span>
              {d?.title && <span className={styles.docTitle}>{d.title}</span>}
            </h1>
            <div className={styles.subline}>
              {status && <span className={`${styles.statusPill} ${statusBand}`}>{status}</span>}
              {d?.document_type && <span className={styles.metaText}>{d.document_type}</span>}
              {contentQ.data?.updated_at_utc && (
                <span className={styles.metaText}>{t('reader.updated', { defaultValue: 'Updated {{t}}', t: dateFmt.format(new Date(contentQ.data.updated_at_utc)) })}</span>
              )}
            </div>
          </div>
          <div className={styles.actions}>
            <Button variant="secondary" onClick={() => window.print()}>{t('reader.print', { defaultValue: 'Print controlled copy' })}</Button>
            <Link to="/documents/$id/edit" params={{ id }} className={styles.editBtn}>{t('reader.edit', { defaultValue: 'Edit' })}</Link>
          </div>
        </div>
      </header>

      <div className={styles.layout}>
        {toc.length > 1 && (
          <aside className={styles.toc} aria-label={t('reader.tocAria', { defaultValue: 'Contents' })}>
            <span className={styles.tocTitle}>{t('reader.contents', { defaultValue: 'On this page' })}</span>
            <nav>
              {toc.map((s) => (
                <a key={s.id} href={`#${s.id}`} className={`${styles.tocLink} ${s.level === 3 ? styles.tocSub : ''}`}>{s.text}</a>
              ))}
            </nav>
          </aside>
        )}
        <article className={styles.paper}>
          {contentQ.isLoading ? (
            <p className={styles.muted}>{t('reader.loading', { defaultValue: 'Loading document…' })}</p>
          ) : (
            // Content is first-party (our content store); safe to render.
            <div className={styles.body} dangerouslySetInnerHTML={{ __html: html }} />
          )}
          <footer className={styles.docFooter}>
            {t('reader.footer', { defaultValue: 'This rendering is a controlled view served by QWise. Printed copies are uncontrolled unless re-verified.' })}
          </footer>
        </article>
      </div>
    </main>
  );
}
